import _ from "lodash";
import React, { useState } from "react";
import classes from "./LECard.scss";
import I18nHOC from "UIComponents/I18nHOC";
import Listview from "UIComponents/Listview";
import Textview from "UIComponents/Textview";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import UIButton from "UIComponents/UIButton";
import { AttachmentList } from "Attachment";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import RubricTable from "Rubric/components/RubricTable";
import SinglePointRubricTable from "SinglePointRubric/components/SinglePointRubricTable";
import ChecklistTable from "Checklist/components/ChecklistTable";
import {
  AssessmentToolPreviewModal,
  AssessmentToolPreviewCard,
} from "UnitPlans/components/LibraryCommonComponents";
import { isAssessmentToolPreviewAvailable } from "Utils";

const styles = {
  attachmentContainerStyle: {
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gridGap: "16px",
  },
  attachmentCardStyle: {
    height: 160,
  },
  rubricConStyle: {
    width: "100%",
  },
};

const LECard = React.memo(
  ({
    t,
    item,
    itemStyle,
    isTeacherEvaluationEnabled,
    isStudentEvaluationEnabled,
  }) => {
    const [
      showAssessmentToolPreview,
      changeShowAssessmentToolPreview,
    ] = useState(false);
    const goals = _.filter(
      _.map(
        _.get(
          _.find(
            _.get(item, "fields", []),
            field => _.get(field, "uid") === "goals"
          ),
          "value",
          []
        ),
        obj => obj.value
      ),
      val => !!val
    );
    const attachments = _.reduce(
      _.get(item, "resources.edges", []),
      (result, obj) => {
        if (!_.isEmpty(_.get(obj, "node.attachments", []))) {
          result = result.concat(_.get(obj, "node.attachments", []));
        }
        return result;
      },
      []
    );

    const voiceInstruction = _.get(
      _.find(
        _.get(item, "fields", []),
        field => _.get(field, "uid") === "voiceInstruction"
      ),
      "resolvedMinimalTree.attachment"
    );

    let studentTemplate = _.get(
      _.find(
        _.get(item, "fields", []),
        field => _.get(field, "uid") === "studentTemplate"
      ),
      "resolvedMinimalTree.attachmentGroups"
    );

    studentTemplate = _.map(studentTemplate, group => {
      return { ...group, isPost: true };
    });

    const assessmentToolType = _.get(
      _.find(
        _.get(item, "fields", []),
        field => _.get(field, "uid") === "measureAssessing"
      ),
      "value",
      ""
    );

    const assessmentToolTypeHasEval = _.get(
      _.find(ASSESSMENT_TOOLS, item => item.type == assessmentToolType),
      "hasEvaluation",
      false
    );

    const assessmentTool = _.get(item, "assessmentTool", {});

    const showAssessmentToolCard =
      (isTeacherEvaluationEnabled || isStudentEvaluationEnabled) &&
      !_.isEmpty(assessmentToolType) &&
      assessmentToolTypeHasEval;

    const getDialogPreview = () => {
      switch (assessmentToolType) {
        case "RUBRIC":
          return (
            <RubricTable
              mode={"view"}
              rubric={assessmentTool}
              conStyle={styles.rubricConStyle}
            />
          );
        case "CHECKLIST":
          return <ChecklistTable mode={"view"} checklist={assessmentTool} />;
        case "SINGLE_POINT_RUBRIC":
          return (
            <SinglePointRubricTable mode={"view"} rubric={assessmentTool} />
          );
        default:
          return null;
      }
    };

    return (
      <div className={classes.container}>
        <div className={classes.cardLabel}>
          {_.get(item, "title.value", "")}
        </div>

        <div className={classes.itemField}>
          <Textview
            labelContainerStyle={{ marginBottom: 0 }}
            label={t(`unitPlanTemplate:le_description_classroom_label`)}
            mode={"view"}
            showViewEmpty={false}
            labelStyle={itemStyle.labelStyle}
            value={_.get(item, "description.value", "")}
          ></Textview>
        </div>
        <div className={classes.itemField}>
          <Listview
            labelContainerStyle={{ marginBottom: 0 }}
            value={goals}
            mode="view"
            showViewEmpty={false}
            label={t(`unitPlanTemplate:le_goals_classroom_label`)}
            labelStyle={itemStyle.labelStyle}
          />
        </div>

        {!!voiceInstruction && (
          <div className={classes.itemField}>
            <AudioRecorderSelector
              mode="view"
              label={"Audio Instructions"}
              value={voiceInstruction}
              name={"voiceInstruction"}
              labelStyle={itemStyle.labelStyle}
            />
          </div>
        )}
        {!!studentTemplate && (
          <div className={classes.itemField}>
            <AttachmentList
              cardType={"vertical"}
              mode={"view"}
              label={"Student template"}
              showViewEmpty={false}
              value={studentTemplate}
              labelStyle={itemStyle.labelStyle}
              attachmentContainerStyle={styles.attachmentContainerStyle}
              cardStyle={styles.attachmentCardStyle}
            />
          </div>
        )}
        {_.get(attachments, "length", 0) > 0 ? (
          <div className={classes.itemField}>
            <AttachmentList
              cardType={"vertical"}
              mode={"view"}
              label={t(`common:resource_label_plural`)}
              showViewEmpty={false}
              value={attachments}
              labelStyle={itemStyle.labelStyle}
              attachmentContainerStyle={styles.attachmentContainerStyle}
              cardStyle={styles.attachmentCardStyle}
            />
          </div>
        ) : null}
        {showAssessmentToolCard && (
          <div className={classes.fieldCon}>
            <div className={classes.fieldLabel}>
              {t("common:le_assessmentTool_label")}
            </div>
            {isAssessmentToolPreviewAvailable({ type: assessmentToolType }) ? (
              <AssessmentToolPreviewCard
                assessmentTool={assessmentTool}
                mode={"view"}
                onClick={() => changeShowAssessmentToolPreview(true)}
                options={null}
                type={assessmentToolType}
              />
            ) : (
              <UIButton
                containerStyle={{ marginTop: "16px" }}
                type={"hollow"}
                color={"tertiary"}
                onClick={() => changeShowAssessmentToolPreview(true)}
              >
                {t("common:view_label", {
                  label: t(
                    _.get(
                      _.find(
                        ASSESSMENT_TOOLS,
                        item => item.type == assessmentToolType
                      ),
                      "locale",
                      ""
                    )
                  ),
                })}
              </UIButton>
            )}
          </div>
        )}
        {showAssessmentToolPreview && (
          <AssessmentToolPreviewModal
            getPreviewModalBody={getDialogPreview}
            tool={assessmentTool}
            title={t(
              _.get(
                _.find(
                  ASSESSMENT_TOOLS,
                  item => item.type == assessmentToolType
                ),
                "locale",
                ""
              )
            )}
            showChooseButton={false}
            onClickClose={() => changeShowAssessmentToolPreview(false)}
          />
        )}
      </div>
    );
  }
);

LECard.displayName = "LECard";

export default I18nHOC({ resource: ["common", "unitPlanTemplate"] })(LECard);
